<template>
  <div class="weekly-view-dashboard-hh w-full max-w-full min-w-0 flex flex-col gap-5">
    <p class="weekly-view-dashboard-hh__intro">
      {{ $t('dashboard_hh_intro') }}
    </p>

    <div
      v-if="loading"
      class="py-8 text-center text-sm text-gray-500"
    >
      {{ $t('loading') }}...
    </div>

    <div
      v-else-if="!disciplines.length"
      class="weekly-view-dashboard-hh__empty"
    >
      {{ $t('no_results') }}
    </div>

    <template v-else>
      <div
        v-for="discipline in disciplines"
        :key="discipline.id"
        class="weekly-view-dashboard-hh__block"
      >
        <h2 class="weekly-view-dashboard-hh__title">
          {{ discipline.name }}
        </h2>
        <div class="weekly-view-dashboard-hh__wrap overflow-x-auto">
          <table class="weekly-view-dashboard-hh__table">
            <thead>
              <tr>
                <th rowspan="2" class="weekly-view-dashboard-hh__th weekly-view-dashboard-hh__th--week">
                  {{ $t('weeks') }}
                </th>
                <th colspan="4" class="weekly-view-dashboard-hh__th">
                  {{ $t('hh') }}
                </th>
                <th colspan="4" class="weekly-view-dashboard-hh__th weekly-view-dashboard-hh__th--eff-group">
                  {{ $t('efficiency') }}
                </th>
              </tr>
              <tr>
                <th
                  v-for="column in hhColumns"
                  :key="column.key"
                  class="weekly-view-dashboard-hh__th weekly-view-dashboard-hh__th--col"
                  :class="{ 'weekly-view-dashboard-hh__th--hh-last': column.key === 'realized' }"
                >
                  {{ $t(column.labelKey) }}
                </th>
                <th
                  v-for="column in efficiencyColumns"
                  :key="column.key"
                  class="weekly-view-dashboard-hh__th weekly-view-dashboard-hh__th--eff"
                >
                  {{ $t(column.labelKey) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in discipline.weeks"
                :key="`${discipline.id}-${row.week}`"
                :class="[
                  index % 2 === 0
                    ? 'weekly-view-dashboard-hh__row--odd'
                    : 'weekly-view-dashboard-hh__row--even',
                  { 'weekly-view-dashboard-hh__row--current': row.week === currentWeekNumber },
                ]"
              >
                <td class="weekly-view-dashboard-hh__td weekly-view-dashboard-hh__td--week">
                  {{ row.weekLabel }}
                </td>
                <td
                  v-for="column in hhColumns"
                  :key="`${row.week}-${column.key}`"
                  class="weekly-view-dashboard-hh__td"
                  :class="{ 'weekly-view-dashboard-hh__td--hh-last': column.key === 'realized' }"
                >
                  {{ formatHours(row.hh[column.key]) }}
                </td>
                <td
                  v-for="column in efficiencyColumns"
                  :key="`${row.week}-eff-${column.key}`"
                  class="weekly-view-dashboard-hh__td weekly-view-dashboard-hh__td--efficiency"
                  :style="efficiencyCellStyle(row.efficiency[column.key])"
                >
                  {{ formatPercent(row.efficiency[column.key]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'

function ratioPct(a, b) {
  if (a == null || b == null || !Number.isFinite(Number(a)) || !Number.isFinite(Number(b)) || Number(b) <= 0) {
    return null
  }
  return Math.round((Number(a) / Number(b)) * 100)
}

function buildWeekRow(summaryRow) {
  const week = Number(summaryRow && summaryRow.week) || 0
  const planned = toNullableNumber(summaryRow && summaryRow.planned)
  const available = toNullableNumber(summaryRow && summaryRow.available)
  const scheduled = toNullableNumber(summaryRow && summaryRow.scheduled)
  const realized = toNullableNumber(summaryRow && summaryRow.realized)

  return {
    week,
    weekLabel: (summaryRow && summaryRow.week_label) || `S-${week}`,
    hh: {
      planned,
      available,
      scheduled,
      realized,
    },
    efficiency: {
      prog_plan: toNullableNumber(summaryRow && summaryRow.prog_plan) ?? ratioPct(scheduled, planned),
      prog_disp: toNullableNumber(summaryRow && summaryRow.prog_disp) ?? ratioPct(scheduled, available),
      real_prog: toNullableNumber(summaryRow && summaryRow.real_prog) ?? ratioPct(realized, scheduled),
      real_disp: toNullableNumber(summaryRow && summaryRow.real_disp) ?? ratioPct(realized, available),
    },
  }
}

function toNullableNumber(value) {
  if (value == null || value === '') {
    return null
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export default {
  name: 'weekly-view-dashboard-hh',

  props: {
    projectId: {
      type: [Number, String],
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    projectWeeks: {
      type: Array,
      default() {
        return []
      },
    },
  },

  data() {
    return {
      loading: false,
      disciplines: [],
      hhColumns: [
        { key: 'planned', labelKey: 'planned' },
        { key: 'available', labelKey: 'available' },
        { key: 'scheduled', labelKey: 'scheduled' },
        { key: 'realized', labelKey: 'realized' },
      ],
      efficiencyColumns: [
        { key: 'prog_plan', labelKey: 'eff_prog_plan' },
        { key: 'prog_disp', labelKey: 'eff_prog_disp' },
        { key: 'real_prog', labelKey: 'eff_real_prog' },
        { key: 'real_disp', labelKey: 'eff_real_disp' },
      ],
    }
  },

  computed: {
    currentWeekNumber() {
      return this.weekNumber > 0 ? Number(this.weekNumber) : null
    },
    maxWeek() {
      const weeks = this.projectWeeks || []
      if (!weeks.length) {
        return this.currentWeekNumber
      }
      return weeks.reduce((max, week) => {
        const n = Number(week && week.number) || 0
        return n > max ? n : max
      }, this.currentWeekNumber || 0)
    },
    weeklyApiParams() {
      return {
        with_takeoff_weekly_summary: 1,
        // Params planos: axios GET serializa objetos como JSON string.
        current_week_number: this.currentWeekNumber,
        current_week_start: this.weekStartDate || null,
        current_week_end: this.weekEndDate || null,
        // Compat: también objeto (backend acepta JSON string o array).
        current_week: {
          number: this.currentWeekNumber,
          start: this.weekStartDate || null,
          end: this.weekEndDate || null,
        },
        previous_weeks: 0,
        max_week: this.maxWeek,
      }
    },
  },

  watch: {
    projectId: {
      immediate: true,
      handler(value) {
        if (value && this.currentWeekNumber) {
          this.loadSummary()
        } else if (!value) {
          this.disciplines = []
        }
      },
    },
    weeklyApiParams: {
      deep: true,
      handler() {
        if (this.projectId && this.currentWeekNumber) {
          this.loadSummary()
        }
      },
    },
  },

  methods: {
    async loadSummary() {
      if (!this.projectId || !this.currentWeekNumber) {
        this.disciplines = []
        return
      }

      this.loading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots`,
          { params: this.weeklyApiParams },
        )
        const list = (data && data.success && data.boq_roots) ? data.boq_roots : []

        this.disciplines = list.map((raw) => {
          const summary = Array.isArray(raw.weekly_hh_summary) ? raw.weekly_hh_summary : []
          // Siempre 3 filas: actual + 2 posteriores (el API ya lo garantiza).
          const weeks = summary.length
            ? summary.map(buildWeekRow)
            : [0, 1, 2].map((offset) => buildWeekRow({
              week: this.currentWeekNumber + offset,
              week_label: `S-${this.currentWeekNumber + offset}`,
              planned: null,
              available: null,
              scheduled: null,
              realized: null,
            }))

          return {
            id: raw.id,
            name: raw.name || raw.code || `#${raw.id}`,
            weeks,
          }
        })
      } catch (error) {
        this.disciplines = []
        this.$notify.error('error_loading_data')
      } finally {
        this.loading = false
      }
    },

    formatHours(value) {
      if (value == null || value === '') {
        return '—'
      }
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return '—'
      }
      if (Math.abs(number) < 0.0001) {
        return '—'
      }
      return number.toFixed(2).replace('.', ',')
    },

    formatPercent(value) {
      if (value == null || value === '') {
        return '—'
      }
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return '—'
      }
      return `${Math.round(number)}%`
    },

    efficiencyCellStyle(value) {
      if (value == null || !Number.isFinite(Number(value))) {
        return { color: '#c7ccd4', backgroundColor: '#fff' }
      }
      const v = Number(value)
      return {
        backgroundColor: `rgba(30, 113, 69, ${Math.min(0.45, (v / 150) * 0.45)})`,
        color: '#1e4d33',
      }
    },
  },
}
</script>

<style scoped>
.weekly-view-dashboard-hh__intro {
  font-size: 0.6875rem;
  color: #6b7280;
  max-width: 48rem;
  margin: 0;
}

.weekly-view-dashboard-hh__empty {
  border-radius: 0.25rem;
  border: 1px solid #d4dae3;
  background: #fff;
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.75rem;
  color: #9ca3af;
}

.weekly-view-dashboard-hh__block {
  align-self: flex-start;
  width: 100%;
  max-width: 56rem;
}

.weekly-view-dashboard-hh__title {
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  margin: 0 0 0.4rem;
  color: #1e4d33;
}

.weekly-view-dashboard-hh__wrap {
  border: 1px solid #d4dae3;
  border-radius: 0.25rem;
  overflow-x: auto;
  background: #fff;
}

.weekly-view-dashboard-hh__table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 0.6875rem;
}

.weekly-view-dashboard-hh__th {
  background-color: #1e7145 !important;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  vertical-align: middle;
  padding: 0.4rem 0.75rem;
  border: 1px solid #145233;
  white-space: nowrap;
  letter-spacing: 0.04em;
}

.weekly-view-dashboard-hh__th--eff-group {
  background-color: #2e8b57 !important;
  border-color: #246b44;
}

.weekly-view-dashboard-hh__th--col {
  background-color: #3b8a5f !important;
}

.weekly-view-dashboard-hh__th--hh-last {
  border-right: 3px solid #0d3d24 !important;
}

.weekly-view-dashboard-hh__th--eff {
  background-color: #48a06c !important;
}

.weekly-view-dashboard-hh__th--week {
  vertical-align: middle;
}

.weekly-view-dashboard-hh__td {
  text-align: right;
  padding: 0.4rem 0.75rem;
  border-top: 1px solid #e2e8f0;
  color: #3d4852;
  font-variant-numeric: tabular-nums;
}

.weekly-view-dashboard-hh__td--week {
  text-align: center;
  font-weight: 700;
  color: #1e4d33;
  border-right: 2px solid #d4dae3;
}

.weekly-view-dashboard-hh__td--hh-last {
  border-right: 3px solid #d4dae3;
}

.weekly-view-dashboard-hh__td--efficiency {
  text-align: center;
  font-weight: 700;
}

.weekly-view-dashboard-hh__row--odd {
  background-color: #fff;
}

.weekly-view-dashboard-hh__row--even {
  background-color: #f8faf9;
}

.weekly-view-dashboard-hh__row--current {
  background-color: #eef7f0;
}
</style>
